
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    Badge,
    useTheme,
    useMediaQuery
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import io from 'socket.io-client';
import axios from 'axios';
import { getApiUrl } from '../config/api';

// Initialize Socket outside component to prevent re-connections
const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
// Only connect when component mounts and we have token
let socket: any;

interface Message {
    id: string;
    content: string;
    senderId: string;
    createdAt: string;
    sender: {
        id: string;
        name: string;
    };
}

interface Chat {
    id: string;
    participants: {
        id: string;
        name: string;
        email: string;
        avatar?: string;
    }[];
    lastMessage?: Message;
    updatedAt: string;
}

const ChatPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [chats, setChats] = useState<Chat[]>([]);
    const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [user, setUser] = useState<any>(null);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    // Initial Load & Socket Setup
    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        const currentUser = userInfo ? JSON.parse(userInfo).user : null;
        setUser(currentUser);

        if (token && !socket) {
            socket = io(SOCKET_URL, {
                extraHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }

        if (socket) {
            // Register user for online status tracking
            if (currentUser) {
                socket.emit('register_user', currentUser.id);
            }

            socket.on('receive_message', (message: Message) => {
                if (selectedChat && currentUser && message.senderId !== currentUser.id) {
                    setMessages(prev => [...prev, message]);
                }
                // Refresh chats to update last message/time
                fetchChats();
            });

            socket.on('online_users', (users: string[]) => {
                setOnlineUsers(users);
            });
        }

        fetchChats();

        return () => {
            if (socket) {
                socket.off('receive_message');
                socket.off('online_users');
            }
        };
    }, [selectedChat]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (selectedChat) {
            fetchMessages(selectedChat.id);
            if (socket) {
                socket.emit('join_chat', selectedChat.id);
            }
        }
    }, [selectedChat]);

    // Search Effect
    useEffect(() => {
        const handleUserSearch = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(`${getApiUrl('chat/search')}?query=${searchTerm}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSearchResults(data);
            } catch (error) {
                console.error('Error searching users:', error);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                handleUserSearch();
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const fetchChats = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const { data } = await axios.get(getApiUrl('chat'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setChats(data);
        } catch (error) {
            console.error('Error fetching chats:', error);
        }
    };

    const fetchMessages = async (chatId: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const { data } = await axios.get(getApiUrl(`chat/${chatId}/messages`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessages(data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const handleStartChat = async (participantId: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const { data } = await axios.post(getApiUrl('chat'), { participantId }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setChats(prev => {
                const exists = prev.find(c => c.id === data.id);
                if (exists) return prev;
                return [data, ...prev];
            });
            setSelectedChat(data);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error) {
            console.error('Error starting chat:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedChat) return;

        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            const { data } = await axios.post(getApiUrl('chat/message'), {
                chatId: selectedChat.id,
                content: newMessage
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Optimistic update
            setMessages(prev => [...prev, data]);
            setNewMessage('');
            fetchChats(); // Update list order/preview

        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const getOtherParticipant = (chat: Chat) => {
        return chat.participants.find(p => p.id !== user?.id) || chat.participants[0];
    };

    const isUserOnline = (userId: string | undefined) => {
        return userId ? onlineUsers.includes(userId) : false;
    };

    return (
        <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', bgcolor: '#F4F7FE', p: { xs: 1, md: 3 } }}>
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    width: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid #edf2f7'
                }}
            >
                {/* Sidebar (Chat List) */}
                <Box
                    sx={{
                        width: isMobile ? (selectedChat ? 0 : '100%') : 320,
                        borderRight: '1px solid #edf2f7',
                        display: isMobile && selectedChat ? 'none' : 'flex',
                        flexDirection: 'column',
                        bgcolor: 'white'
                    }}
                >
                    <Box sx={{ p: 2, borderBottom: '1px solid #edf2f7' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={700} color="#263B7C">Messages</Typography>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '8px', bgcolor: '#F8FAFC', '& fieldset': { border: 'none' } } // Remove border
                            }}
                        />
                        {searchResults.length > 0 && (
                            <List sx={{ position: 'absolute', zIndex: 10, bgcolor: 'white', width: 280, boxShadow: 3, borderRadius: 2, mt: 1 }}>
                                {searchResults.map(user => (
                                    <ListItem key={user.id} component="li" disablePadding>
                                        {/* Changed to component="li" and manually managed click/hover via Box/div inside if needed or just standar button */}
                                        <Box
                                            onClick={() => handleStartChat(user.id)}
                                            sx={{
                                                width: '100%', p: 1, display: 'flex', alignItems: 'center', cursor: 'pointer',
                                                '&:hover': { bgcolor: '#f5f5f5' }
                                            }}
                                        >
                                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>{user.name.charAt(0)}</Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{user.role}</Typography>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>

                    <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
                        {chats.map((chat) => {
                            const otherUser = getOtherParticipant(chat);
                            const isOnline = isUserOnline(otherUser?.id);

                            return (
                                <React.Fragment key={chat.id}>
                                    <ListItem
                                        component="div"
                                        onClick={() => setSelectedChat(chat)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: selectedChat?.id === chat.id ? '#F4F7FE' : 'transparent',
                                            '&:hover': { bgcolor: '#F8FAFC' }
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Badge
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                variant="dot"
                                                color={isOnline ? "success" : "default"}
                                                sx={{ '& .MuiBadge-badge': { backgroundColor: isOnline ? '#44b700' : 'transparent' } }}
                                            >
                                                <Avatar alt={otherUser?.name} src={otherUser?.avatar || '/static/images/avatar/1.jpg'} />
                                            </Badge>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" fontWeight={600} color="#2D3748">
                                                    {otherUser?.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {chat.lastMessage?.content || 'Started a conversation'}
                                                </Typography>
                                            }
                                        />
                                        <Box>
                                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                                                {new Date(chat.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                    <Divider component="li" />
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Box>

                {/* Main Chat Area */}
                <Box
                    sx={{
                        flex: 1,
                        display: isMobile && !selectedChat ? 'none' : 'flex',
                        flexDirection: 'column',
                        bgcolor: '#FAFBFF'
                    }}
                >
                    {selectedChat ? (
                        <>
                            {/* Chat Header */}
                            <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {isMobile && (
                                        <IconButton onClick={() => setSelectedChat(null)}>
                                            <ArrowBackIcon />
                                        </IconButton>
                                    )}
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        variant="dot"
                                        invisible={!isUserOnline(getOtherParticipant(selectedChat)?.id)}
                                        color="success"
                                        sx={{ '& .MuiBadge-badge': { backgroundColor: '#44b700' } }}
                                    >
                                        <Avatar src={getOtherParticipant(selectedChat)?.avatar} />
                                    </Badge>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700} color="#2D3748">
                                            {getOtherParticipant(selectedChat)?.name}
                                        </Typography>
                                        <Typography variant="caption" color={isUserOnline(getOtherParticipant(selectedChat)?.id) ? "success.main" : "text.secondary"} fontWeight={500}>
                                            {isUserOnline(getOtherParticipant(selectedChat)?.id) ? "Online" : "Offline"}
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton>
                                    <MoreVertIcon />
                                </IconButton>
                            </Box>

                            {/* Messages List */}
                            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {messages.map((msg) => {
                                    const isMe = msg.senderId === user?.id;
                                    return (
                                        <Box
                                            key={msg.id}
                                            sx={{
                                                alignSelf: isMe ? 'flex-end' : 'flex-start',
                                                maxWidth: '70%',
                                                minWidth: '60px'
                                            }}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    bgcolor: isMe ? '#263B7C' : 'white',
                                                    color: isMe ? 'white' : '#2D3748',
                                                    borderRadius: isMe ? '16px 16px 0 16px' : '16px 16px 16px 0',
                                                    border: isMe ? 'none' : '1px solid #E2E8F0'
                                                }}
                                            >
                                                <Typography variant="body2">{msg.content}</Typography>
                                            </Paper>
                                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', textAlign: isMe ? 'right' : 'left' }}>
                                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input Area */}
                            <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #edf2f7' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton size="small" sx={{ color: '#A0AEC0' }}>
                                        <AttachFileIcon />
                                    </IconButton>
                                    <TextField
                                        fullWidth
                                        placeholder="Type your message..."
                                        size="small"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '20px',
                                                bgcolor: '#F7FAFC',
                                                '& fieldset': { border: 'none' }
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={handleSendMessage}
                                        sx={{
                                            bgcolor: '#F9B43F',
                                            color: 'white',
                                            '&:hover': { bgcolor: '#e0a238' }
                                        }}
                                    >
                                        <SendIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#A0AEC0' }}>
                            <Box sx={{ p: 4, bgcolor: '#F0F4FF', borderRadius: '50%', mb: 2 }}>
                                <SendIcon sx={{ fontSize: 40, color: '#263B7C' }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="#2D3748">Select a Conversation</Typography>
                            <Typography variant="body2">Choose a contact to start messaging</Typography>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ChatPage;
